<script setup lang="ts">
import { computed } from 'vue';

import { OverlayTeleport } from '@/shared/ui/overlay';

interface Props {
  anchorPercent: number;
  panel?: boolean;
  placement?: 'above' | 'inside';
  viewportX?: number;
  viewportY?: number;
}

const props = withDefaults(defineProps<Props>(), {
  panel: false,
  placement: 'inside',
  viewportX: undefined,
  viewportY: undefined,
});

const normalizedAnchorPercent = computed(() => Math.min(100, Math.max(0, props.anchorPercent)));
const hasViewportAnchor = computed(() => Number.isFinite(props.viewportX) && Number.isFinite(props.viewportY));
const alignmentClass = computed(() => {
  if (normalizedAnchorPercent.value <= 12) {
    return 'translate-x-0';
  }

  if (normalizedAnchorPercent.value >= 88) {
    return '-translate-x-full';
  }

  return '-translate-x-1/2';
});
const positionStyle = computed(() => {
  if (!hasViewportAnchor.value) {
    return { left: `${normalizedAnchorPercent.value}%` };
  }

  if (!props.panel) {
    return { left: `${props.viewportX}px`, top: `${props.viewportY}px` };
  }

  const panelWidth = 256;
  const viewportGap = 12;
  const maximumLeft = Math.max(viewportGap, window.innerWidth - panelWidth - viewportGap);
  const left = Math.min(maximumLeft, Math.max(viewportGap, (props.viewportX ?? viewportGap) - panelWidth / 2));

  return {
    left: `${left}px`,
    top: `${Math.max(viewportGap, props.viewportY ?? viewportGap)}px`,
  };
});
</script>

<template>
  <!-- BaseTooltip привязан к DOM-триггеру и не умеет следовать за virtual anchor внутри SVG. -->
  <OverlayTeleport :disabled="!hasViewportAnchor">
    <div
      aria-hidden="true"
      class="pointer-events-none rounded-lg border border-(--border) bg-(--surface-overlay) text-xs text-(--text-primary) shadow-xl"
      :class="[
        hasViewportAnchor
          ? ['fixed z-(--popup-z-index)', !panel && [alignmentClass, placement === 'above' && '-translate-y-full']]
          : ['absolute z-10', alignmentClass, placement === 'above' ? 'top-0 -translate-y-full' : 'top-3'],
        panel ? 'w-64 max-w-[calc(100vw-1.5rem)] p-3' : 'px-2.5 py-1.5 font-medium whitespace-nowrap',
      ]"
      :style="positionStyle"
    >
      <slot />
    </div>
  </OverlayTeleport>
</template>
